/**
 * Cross-partner report downloads from the portal API (src/client/portal).
 *
 * The CSV endpoints are described as `string` bodies in the spec, so they are
 * fetched with `parseAs: 'blob'` and handed to the browser as files; the KPI
 * endpoints content-negotiate between CSV and JSON, so the JSON readers send an
 * explicit Accept header (as the webapp's use-kpi-report does).
 */
import {
  getReportsBankTransaction,
  getReportsClaimSummary,
  getReportsFullTransaction,
  getReportsKpi,
  getReportsKpiCombined,
  getReportsKpiList,
  getReportsScheduledPayments,
  getReportsSummary,
  getReportsTransaction,
} from '../client/portal/sdk.gen';
import type {
  GetReportsKpiData,
  KpiInfo,
  KpiReport,
  PartnerName,
} from '../client/portal/types.gen';
import { describeApiError } from '../lib/sdk';

export type { KpiInfo, KpiKind, KpiReport, KpiReportRow, TimeGranularity } from '../client/portal/types.gen';

/** Query of the KPI endpoints; `partitionBy` and `kpiName` only exist there. */
export type KpiQuery = GetReportsKpiData['query'];
export type KpiName = KpiQuery['kpiName'];
export type PartitionBy = KpiQuery['partitionBy'];

/** One KPI report per partner, or the selected partners combined into one. */
export type KpiMode = 'perPartner' | 'combined';

// Portal.Server.Reports.PartnerFailure / PartnerFetchFailure: the JSON body of
// the 502 a report answers with when some partners could not be fetched from.
// Nothing is combined then; `succeeded` names the partners that did answer, so
// the caller can ask again for just those. Hand-written because servant does
// not describe error bodies in the OpenAPI spec.
export type PartnerFailure = {
  partnerName: PartnerName;
  reason: string;
};

export type PartnerFetchFailure = {
  message: string;
  failed: PartnerFailure[];
  succeeded: PartnerName[];
};

// A report the portal could not assemble because some partners did not
// answer. The message is the server's; `failed` and `succeeded` say who, so
// the caller can offer to ask again for only the partners that have it.
export class ReportFetchError extends Error {
  failed: PartnerFailure[];

  succeeded: PartnerName[];

  constructor(failure: PartnerFetchFailure) {
    super(failure.message);
    this.name = 'ReportFetchError';
    this.failed = failure.failed;
    this.succeeded = failure.succeeded;
  }
}

export const isReportFetchError = (
  error: unknown
): error is ReportFetchError => error instanceof ReportFetchError;

// The generated client rejects with the parsed error body; a partial fan-out
// failure is the JSON object above, anything else is text (or nothing).
const asPartnerFetchFailure = (error: unknown): PartnerFetchFailure | null => {
  if (typeof error !== 'object' || error === null) return null;
  const candidate = error as Partial<PartnerFetchFailure>;
  return typeof candidate.message === 'string' &&
    Array.isArray(candidate.failed) &&
    Array.isArray(candidate.succeeded)
    ? (candidate as PartnerFetchFailure)
    : null;
};

// Like lib/sdk's `sdk`, but a partial fan-out failure is rethrown as
// ReportFetchError so the views can offer to narrow the partner selection.
const reportSdk = async <T>(
  label: string,
  call: () => Promise<{ data: T }>
): Promise<T> => {
  try {
    return (await call()).data;
  } catch (error) {
    const failure = asPartnerFetchFailure(error);
    if (failure !== null) throw new ReportFetchError(failure);
    throw describeApiError(label, error);
  }
};

type DayRange = { startDay: string; endDay: string };
// Omitting `partners` means every partner the user has access to; the caller
// must not pass an empty list (the UI disables downloads instead).
type PartnerSelection = { partners?: PartnerName[] };

const csv = { headers: { Accept: 'text/csv' }, parseAs: 'blob' } as const;

// The generated functions type the CSV body as `string` (its schema); with
// parseAs: 'blob' the client hands back a Blob.
const asBlob = (data: unknown): Blob => data as Blob;

/** The spec calls the repeated partner parameter `partner`. */
const toQuery = <T extends PartnerSelection>({
  partners,
  ...rest
}: T): Omit<T, 'partners'> & { partner?: PartnerName[] } => ({
  ...rest,
  ...(partners && partners.length > 0 ? { partner: partners } : {}),
});

/** The CSV reports, keyed as the reports view names them. */
export const csvReports = {
  transaction: (query: DayRange & PartnerSelection) =>
    getReportsTransaction({ ...csv, query: toQuery(query) }),
  fullTransaction: (query: DayRange & PartnerSelection) =>
    getReportsFullTransaction({ ...csv, query: toQuery(query) }),
  summary: (query: PartnerSelection) =>
    getReportsSummary({ ...csv, query: toQuery(query) }),
  claimSummary: (query: PartnerSelection) =>
    getReportsClaimSummary({ ...csv, query: toQuery(query) }),
  bankTransaction: (query: PartnerSelection) =>
    getReportsBankTransaction({ ...csv, query: toQuery(query) }),
  scheduledPayments: (query: PartnerSelection) =>
    getReportsScheduledPayments({ ...csv, query: toQuery(query) }),
};

export type CsvReport = keyof typeof csvReports;

export const downloadReportCsv = async (
  report: CsvReport,
  options: Partial<DayRange> & PartnerSelection
): Promise<Blob> => {
  const { startDay, endDay, partners } = options;
  const label = `Could not download the ${report} report`;
  if (report === 'transaction' || report === 'fullTransaction') {
    // Required by the API for these two; the card's date pickers are not
    // clearable, so this only guards the type.
    if (!startDay || !endDay) {
      throw new Error(`${label}: a start and end day are required`);
    }
    return asBlob(
      await reportSdk(label, () =>
        csvReports[report]({ startDay, endDay, partners })
      )
    );
  }
  return asBlob(await reportSdk(label, () => csvReports[report]({ partners })));
};

// Both KPI endpoints take the same query: the series over the periods
// overlapping [startDay, endDay] (yyyy-MM-dd, both inclusive); `partners` as
// for the CSV reports.
export type KpiRequest = Omit<KpiQuery, 'partner'> & PartnerSelection;

const kpiCall = (mode: KpiMode) =>
  mode === 'combined' ? getReportsKpiCombined : getReportsKpi;

export const downloadKpiCsv = async (
  mode: KpiMode,
  query: KpiRequest
): Promise<Blob> => {
  // `unknown`: the two endpoints differ in their (JSON) data type, and the
  // CSV body is a Blob regardless.
  const data = await reportSdk<unknown>('Could not download the KPI report', () =>
    kpiCall(mode)({ ...csv, query: toQuery(query) })
  );
  return asBlob(data);
};

const json = { headers: { Accept: 'application/json' } } as const;

export const getKpiReports = (query: KpiRequest): Promise<KpiReport[]> =>
  reportSdk('Could not fetch the KPI reports', () =>
    getReportsKpi({ ...json, query: toQuery(query) })
  );

export const getCombinedKpiReport = (query: KpiRequest): Promise<KpiReport> =>
  reportSdk('Could not fetch the combined KPI report', () =>
    getReportsKpiCombined({ ...json, query: toQuery(query) })
  );

export const getKpiList = (): Promise<KpiInfo[]> =>
  reportSdk('Could not fetch the KPI list', () => getReportsKpiList());
